import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LogOut, Menu, X, ChevronDown, ChevronRight, ArrowLeftRight, Award, Shield,
} from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { NotificationCenter } from './NotificationCenter';
import { MENU_GROUPS } from '../config/menuConfig';

const API_URL = window.location.origin + '/api';

function adjustColor(hex, amount) {
  let c = hex.replace('#', '');
  if (c.length === 3) c = c[0]+c[0]+c[1]+c[1]+c[2]+c[2];
  const num = parseInt(c, 16);
  let r = Math.min(255, Math.max(0, (num >> 16) + amount));
  let g = Math.min(255, Math.max(0, ((num >> 8) & 0x00FF) + amount));
  let b = Math.min(255, Math.max(0, (num & 0x0000FF) + amount));
  return `#${(r << 16 | g << 8 | b).toString(16).padStart(6, '0')}`;
}

export const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, hasPermission, getAuthHeaders, tenantSlug, token } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [badges, setBadges] = useState({});
  const [companies, setCompanies] = useState([]);
  const [showCompanySelector, setShowCompanySelector] = useState(false);
  const [companySearch, setCompanySearch] = useState('');
  const [theme, setTheme] = useState(null);
  const navScrollRef = useRef(null);

  const isManagerOrAdmin = ['ADMIN', 'MANAGER', 'GESTOR'].includes(user?.role?.toUpperCase());
  const isAdmin = user?.role?.toUpperCase() === 'ADMIN';

  // Load company theme
  useEffect(() => {
    if (!user?.company_id) return;
    axios.get(`${API_URL}/company/theme/${user.company_id}`)
      .then(res => setTheme(res.data || null))
      .catch(() => {});
  }, [user?.company_id]);

  // Apply theme colors as CSS variables
  const themeStyle = theme ? {
    '--sidebar-primary': theme.primary_color || '#4a3728',
    '--sidebar-secondary': theme.secondary_color || '#d4a574',
  } : {};

  const sidebarBg = theme?.primary_color
    ? { background: `linear-gradient(to bottom, ${theme.primary_color}, ${adjustColor(theme.primary_color, -20)})` }
    : {};

  const accentColor = theme?.secondary_color || '#d4a574';

  const fetchBadges = useCallback(async () => {
    if (!isManagerOrAdmin) return;
    try {
      const res = await axios.get(`${API_URL}/notifications/badges`, { headers: getAuthHeaders() });
      setBadges(res.data || {});
    } catch { /* silent */ }
  }, [isManagerOrAdmin, getAuthHeaders]);

  useEffect(() => {
    fetchBadges();
    const interval = setInterval(fetchBadges, 30000);
    return () => clearInterval(interval);
  }, [fetchBadges]);

  // Restore sidebar scroll position after navigation
  useEffect(() => {
    const saved = sessionStorage.getItem('sidebar-scroll');
    if (saved && navScrollRef.current) {
      navScrollRef.current.scrollTop = parseInt(saved, 10);
    }
  });

  // Load companies for super admin selector
  useEffect(() => {
    if (!user?.is_super_admin) return;
    let cancelled = false;
    axios.get(`${API_URL}/platform-admin/companies`, { headers: getAuthHeaders() })
      .then(res => { if (!cancelled) setCompanies(res.data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [user?.is_super_admin]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load current company name for non-super-admin users
  const [companyName, setCompanyName] = useState('');
  useEffect(() => {
    if (!tenantSlug || companies.length > 0) return;
    axios.get(`${API_URL}/auth/tenant/${tenantSlug}`)
      .then(res => setCompanyName(res.data?.name || ''))
      .catch(() => {});
  }, [tenantSlug, companies.length]);

  const handleSwitchCompany = (slug) => {
    setShowCompanySelector(false);
    setCompanySearch('');
    // Update stored tenant slug before reload
    localStorage.setItem('tenant_slug', slug);
    // Get current sub-path after tenant prefix
    const currentPath = location.pathname;
    const tenantPrefix = `/t/${tenantSlug}`;
    const subPath = currentPath.startsWith(tenantPrefix) ? currentPath.substring(tenantPrefix.length) : '/dashboard';
    navigate(`/t/${slug}${subPath || '/dashboard'}`);
    window.location.reload(); // Reload to reset all data for new tenant
  };

  const currentCompany = companies.find(c => c.slug === tenantSlug);
  const filteredCompanies = companies.filter(c =>
    c.name.toLowerCase().includes(companySearch.toLowerCase()) ||
    c.slug.toLowerCase().includes(companySearch.toLowerCase())
  );

  // Close company selector when clicking outside
  useEffect(() => {
    if (!showCompanySelector) return;
    const handleClick = (e) => {
      if (!e.target.closest('[data-testid="company-selector-container"]')) {
        setShowCompanySelector(false);
        setCompanySearch('');
      }
    };
    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, [showCompanySelector]);

  const tp = tenantSlug ? `/t/${tenantSlug}` : '';

  const badgeMap = {
    '/redemptions': badges.pending_redemptions || 0,
    '/materials': badges.low_stock_materials || 0,
    '/work-orders': badges.unassigned_wo || 0,
    '/stock-alerts': badges.low_stock_materials || 0,
    '/purchase-orders': badges.pending_po_confirmations || 0,
    '/notifications': badges.unread_notifications || 0,
    '/reports/validate': badges.pending_reports_validation || 0,
    '/my-work-orders': badges.my_open_wo || 0,
    '/my-reports': badges.my_pending_reports || 0,
    '/my-calls': badges.my_open_calls || 0,
    '/material-request': badges.my_pending_material_requests || 0,
  };

  // Build visible groups from MENU_CONFIG
  const visibleGroups = MENU_GROUPS.map(group => {
    const visibleItems = group.items.filter(item => {
      if (item.adminOnly && isAdmin) return true;
      if (item.adminOnly && !item.permission) return false;
      if (!item.permission) return true;
      return hasPermission(item.permission);
    });
    return { ...group, items: visibleItems };
  }).filter(g => g.items.length > 0);

  // Track which groups are open (default: first group open, others closed)
  const [openGroups, setOpenGroups] = useState(() => {
    const initial = {};
    MENU_GROUPS.forEach(g => { initial[g.key] = g.defaultOpen; });
    // Also open the group that contains the current active path
    return initial;
  });

  // Auto-open the group that contains the active route
  useEffect(() => {
    const currentPath = location.pathname.replace(`/t/${tenantSlug}`, '');
    for (const group of MENU_GROUPS) {
      if (group.items.some(item => currentPath === item.path || currentPath.startsWith(item.path + '/'))) {
        setOpenGroups(prev => ({ ...prev, [group.key]: true }));
        break;
      }
    }
  }, [location.pathname, tenantSlug]);

  const toggleGroup = (key) => {
    setOpenGroups(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Removed old visibleItems - now using visibleGroups above

  const sidebarContent = (
    <>
      <div className="p-6 border-b" style={{ borderColor: `${accentColor}33` }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {theme?.logo_url ? (
              <div className="w-full max-w-[180px] h-16 flex items-center justify-center">
                <img src={theme.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" />
              </div>
            ) : (
              <>
                <div className="w-10 h-10 rounded-lg overflow-hidden">
                  <img src="/uiara-favicon-180x180.png" alt="Uiara" className="w-full h-full object-contain" />
                </div>
                <div>
                  <h1 className="text-xl font-heading font-bold text-white">Uiara Guardian</h1>
                  <p className="text-xs truncate max-w-[140px]" style={{ color: `${accentColor}b3` }}>{currentCompany?.name || companyName || tenantSlug || 'Empresa'}</p>
                </div>
              </>
            )}
          </div>
          <NotificationCenter />
        </div>

        {/* Company Selector for Super Admins */}
        {user?.is_super_admin && companies.length > 1 && (
          <div className="mt-3 relative" data-testid="company-selector-container">
            <button
              onClick={() => setShowCompanySelector(!showCompanySelector)}
              data-testid="company-selector-toggle"
              className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-amber-300 text-xs font-medium hover:bg-amber-500/15 transition-colors"
            >
              <div className="flex items-center gap-2 min-w-0">
                <ArrowLeftRight className="w-3.5 h-3.5 flex-shrink-0" />
                <span className="truncate">{currentCompany?.name || tenantSlug}</span>
              </div>
              <ChevronDown className={`w-3.5 h-3.5 flex-shrink-0 transition-transform ${showCompanySelector ? 'rotate-180' : ''}`} />
            </button>

            {showCompanySelector && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-zinc-900 border border-zinc-700 rounded-lg shadow-xl z-50 max-h-64 overflow-hidden" data-testid="company-selector-dropdown">
                <div className="p-2 border-b border-zinc-800">
                  <input
                    type="text"
                    placeholder="Buscar empresa..."
                    value={companySearch}
                    onChange={e => setCompanySearch(e.target.value)}
                    className="w-full px-2.5 py-1.5 bg-zinc-800 border border-zinc-700 rounded text-xs text-white placeholder-zinc-500 focus:outline-none focus:border-amber-500/50"
                    data-testid="company-search-input"
                    autoFocus
                  />
                </div>
                <div className="overflow-y-auto max-h-48">
                  {filteredCompanies.map(c => (
                    <button
                      key={c.id}
                      onClick={() => handleSwitchCompany(c.slug)}
                      data-testid={`switch-to-${c.slug}`}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-zinc-800 transition-colors flex items-center justify-between ${
                        c.slug === tenantSlug ? 'text-amber-400 bg-amber-500/5' : 'text-zinc-300'
                      }`}
                    >
                      <div className="min-w-0">
                        <p className="font-medium truncate">{c.name}</p>
                        <p className="text-zinc-500 text-[10px]">/t/{c.slug} &middot; {c.user_count} usuario(s)</p>
                      </div>
                      {c.slug === tenantSlug && (
                        <span className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-amber-400 ml-2" />
                      )}
                    </button>
                  ))}
                  {filteredCompanies.length === 0 && (
                    <p className="text-xs text-zinc-500 text-center py-3">Nenhuma empresa encontrada</p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div
        className="flex-1 px-3 py-2 overflow-y-auto"
        ref={navScrollRef}
        onScroll={(e) => { sessionStorage.setItem('sidebar-scroll', e.currentTarget.scrollTop); }}
      >
        {visibleGroups.map((group) => {
          const isGroupOpen = openGroups[group.key];
          const hasActiveItem = group.items.some(item => {
            const fp = `${tp}${item.path}`;
            return location.pathname === fp || location.pathname.startsWith(fp + '/');
          });
          return (
            <div key={group.key} className="mb-1">
              <button
                onClick={() => toggleGroup(group.key)}
                className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-xs font-semibold uppercase tracking-wider transition-colors ${
                  hasActiveItem ? 'text-white/90' : 'text-white/40 hover:text-white/60'
                }`}
                data-testid={`menu-group-${group.key}`}
              >
                <span>{group.label}</span>
                {isGroupOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              </button>
              {isGroupOpen && (
                <div className="space-y-0.5 mt-0.5">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const fullPath = `${tp}${item.path}`;
                    const isActive = location.pathname === fullPath || location.pathname.startsWith(fullPath + '/');
                    const count = badgeMap[item.path] || 0;
                    return (
                      <Link
                        key={item.path}
                        to={fullPath}
                        data-testid={`sidebar-${item.key}`}
                        onClick={() => setMobileOpen(false)}
                        className={`flex items-center gap-3 px-4 py-2 rounded-lg text-sm smooth-transition ${
                          isActive ? 'border' : 'text-white/70 hover:bg-white/5 hover:text-white'
                        }`}
                        style={isActive ? { background: `${accentColor}33`, color: accentColor, borderColor: `${accentColor}4d` } : {}}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="font-medium flex-1">{item.label}</span>
                        {count > 0 && (
                          <span className="min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold">{count > 99 ? '99+' : count}</span>
                        )}
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="p-4 border-t" style={{ borderColor: `${accentColor}33` }}>
        <div className="mb-4 p-4 rounded-lg bg-white/5 backdrop-blur-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm" style={{ background: `${accentColor}33`, color: accentColor }}>
              {user?.full_name?.charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
              <p className="text-xs truncate" style={{ color: `${accentColor}99` }}>{user?.email}</p>
            </div>
          </div>
          {!isAdmin && user?.current_patent && (
            <div className="flex items-center gap-2 text-xs text-white/50">
              <Award className="w-4 h-4" style={{ color: accentColor }} />
              <span className="capitalize">{user?.current_patent}</span>
            </div>
          )}
        </div>
        {user?.is_super_admin && (
          <Link
            to="/admin"
            data-testid="sidebar-super-admin"
            className="flex items-center gap-3 px-4 py-2 mb-2 rounded-lg text-amber-400 hover:bg-amber-500/10 transition-colors text-sm font-medium"
          >
            <Shield className="w-4 h-4" />
            <span>Painel Super Admin</span>
          </Link>
        )}
        <Button
          onClick={handleLogout}
          data-testid="logout-button"
          variant="ghost"
          className="w-full justify-start gap-3 text-white/70 hover:text-white hover:bg-white/5"
        >
          <LogOut className="w-5 h-5" />
          <span>Sair</span>
        </Button>
      </div>
    </>
  );

  return (
    <>
      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-40 border-b px-4 py-3" style={{ background: theme?.primary_color || '#4a3728', borderColor: `${accentColor}33` }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            {theme?.logo_url ? (
              <img src={theme.logo_url} alt="Logo" className="h-8 object-contain" />
            ) : (
              <>
                <img src="/uiara-favicon-32x32.png" alt="Uiara" className="w-6 h-6" />
                <h1 className="text-lg font-heading font-bold text-white">Uiara Guardian</h1>
              </>
            )}
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="text-white"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden lg:flex lg:w-72 lg:flex-col lg:fixed lg:inset-y-0 z-30 shadow-2xl" style={sidebarBg}>
        {sidebarContent}
      </div>

      {/* Mobile Sidebar */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setMobileOpen(false)}>
          <div
            className="fixed inset-y-0 left-0 w-72 shadow-2xl"
            style={sidebarBg}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col h-full">
              {sidebarContent}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
